import { useNavigation } from "@remix-run/react";
import { useEffect, useRef, useState } from "react";

type NavigationProgressState = {
  isNavigating: boolean;
  progress: number;
  isComplete: boolean;
};

export const useNavigationProgress = (): NavigationProgressState => {
  const navigation = useNavigation();
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const animationRef = useRef<number>();
  const timeoutRef = useRef<NodeJS.Timeout>();

  const isNavigating = navigation.state === "loading";

  useEffect(() => {
    // Clean up any existing animations
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!isNavigating) {
      // Navigation completed, jump to completion with a slight delay for visual effect
      timeoutRef.current = setTimeout(() => {
        setProgress(100);
        setIsComplete(true);

        // Hide progress bar after completion is visible
        const hideTimer = setTimeout(() => {
          setProgress(0);
          setIsComplete(false);
        }, 300);

        return () => clearTimeout(hideTimer);
      }, 150);

      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    } else {
      // Navigation started, reset progress
      setProgress(0);
      setIsComplete(false);

      // Simulate progress with moderate timing
      const startTime = Date.now();
      const duration = 2000; // Increased duration for slower progress
      let lastProgress = 0;

      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        // Use a more gradual curve for slower initial progress
        const progressRatio = elapsed / duration;
        const curvedProgress = Math.pow(progressRatio, 0.7) * 100; // Slower initial, then speeds up
        const baseProgress = Math.min(curvedProgress, 75); // Cap at 75% to leave room for completion jump

        // Add minimal randomness for natural feel
        const randomFactor = Math.random() * 0.02;
        const smoothProgress = Math.min(baseProgress + randomFactor, 75);

        // Only update if progress has meaningfully changed
        if (Math.abs(smoothProgress - lastProgress) > 0.3) {
          setProgress(smoothProgress);
          lastProgress = smoothProgress;
        }

        if (smoothProgress < 75) {
          animationRef.current = requestAnimationFrame(updateProgress);
        }
      };

      // Start progress animation with a small delay
      timeoutRef.current = setTimeout(() => {
        animationRef.current = requestAnimationFrame(updateProgress);
      }, 100);

      return () => {
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }
  }, [isNavigating]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isNavigating,
    progress,
    isComplete,
  };
};
